import { useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState, Pagination, SendErrorReportButton } from "@/shared/components";
import { Prompt } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import {
  useItemRequestsListPage,
  useItemRequestsMutations,
  useItemRequestsModals,
} from "../../hooks";
import type { PromptState } from "../../hooks";
import type { RejectRequestDto, FulfillRequestDto, MarkUnavailableDto } from "../../types";
import ItemRequestsTable from "../../components/ItemRequestsTable";
import { RequestsHeader, RequestsStatCards, RequestsFilter, RequestsPageSkeleton } from "./components";

const RequestDetailModal = lazy(() =>
  import("../../components/RequestDetailModal").then((m) => ({ default: m.default })),
);
const FulfillRequestModal = lazy(() =>
  import("../../components/FulfillRequestModal").then((m) => ({ default: m.default })),
);
const MarkUnavailableModal = lazy(() =>
  import("../../components/MarkUnavailableModal").then((m) => ({ default: m.default })),
);

export default function WarehouseRequestsPage() {
  const { user } = useAuth();
  const { t: tWarehouse } = useTranslation("WarehousePage");
  const [prompt, setPrompt] = useState<PromptState | null>(null);

  const listPage = useItemRequestsListPage("WarehouseRequestsPage");
  const requestsMutations = useItemRequestsMutations(setPrompt);
  const requestsModals = useItemRequestsModals();

  if (listPage.isLoading && !listPage.data) {
    return (
      <>
        <title>{listPage.t("meta.title")}</title>
        <section className="flex-1">
          <RequestsHeader />
          <RequestsPageSkeleton />
        </section>
      </>
    );
  }

  if (listPage.isError) {
    return (
      <>
        <title>{listPage.t("meta.title")}</title>
        <section className="flex-1">
          <RequestsHeader />
          <p className="mt-4 text-body-regular-md content-action-negative">{listPage.t("messages.error")}</p>
          <SendErrorReportButton className="mt-3" />
        </section>
      </>
    );
  }

  function handleReject(id: number, reason: string) {
    requestsMutations.handleReject(id, { reason } as RejectRequestDto);
    requestsModals.setRequestDetailModal(null);
  }

  function handleOpenFulfill(id: number) {
    requestsModals.setRequestDetailModal(null);
    requestsModals.setFulfillModal(id);
  }

  function handleOpenMarkUnavailable(id: number) {
    requestsModals.setRequestDetailModal(null);
    requestsModals.setMarkUnavailableModal(id);
  }

  async function handleMarkUnavailableConfirm(requestId: number, reason: string) {
    await requestsMutations.handleMarkUnavailable(requestId, { reason } as MarkUnavailableDto);
    requestsModals.setMarkUnavailableModal(null);
  }

  async function handleFulfillConfirm(requestId: number, unitIds: number[]) {
    await requestsMutations.handleFulfill(requestId, { unit_ids: unitIds } as FulfillRequestDto);
  }

  return (
    <>
      <title>{listPage.t("meta.title")}</title>
      <section className="flex-1">
        <RequestsHeader />
        <RequestsStatCards stats={listPage.stats} />

        <div className="radius-3xl surface-base-fill p-7">
          <RequestsFilter
            search={listPage.search}
            onSearchChange={listPage.setSearch}
            dateRange={listPage.dateRange}
            onDateRangeChange={listPage.setDateRange}
            statusFilter={listPage.statusFilter}
            onStatusChange={listPage.setStatusFilter}
            locale={listPage.locale}
            hasActiveFilters={listPage.activeFilters}
            onReset={listPage.handleResetFilters}
          />

          {listPage.filteredItems.length === 0 ? (
            <EmptyState
              hasActiveFilters={listPage.activeFilters}
              noResultsText={listPage.t("table.noResults")}
              noDataText={listPage.t("table.noData")}
              noDataHint={listPage.t("table.noDataHint")}
            />
          ) : (
            <>
              <ItemRequestsTable
                items={listPage.pageItems}
                onOpenDetail={(id) => requestsModals.setRequestDetailModal(id)}
                sortConfig={listPage.sortConfig}
                onSort={listPage.handleSort}
              />

              <Pagination
                currentPage={listPage.pagination.page}
                totalPages={listPage.pagination.totalPages}
                onPageChange={listPage.pagination.setPage}
                fromItem={listPage.pagination.fromItem}
                toItem={listPage.pagination.toItem}
                total={listPage.sortedItems.length}
              />
            </>
          )}
        </div>
      </section>

      {requestsMutations.approvingId !== null && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tWarehouse("requests.messages.approveLoaderText")}
          additionalText={tWarehouse("requests.messages.approveLoaderAdditional")}
          namespace="WarehousePage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => setPrompt(null)}
          namespace="WarehouseRequestsPage"
        />
      )}

      {requestsModals.requestDetailModal !== null && (
        <Suspense fallback={null}>
          <RequestDetailModal
            requestId={requestsModals.requestDetailModal}
            onClose={() => requestsModals.setRequestDetailModal(null)}
            onApprove={requestsMutations.handleApprove}
            onReject={handleReject}
            onCancel={requestsMutations.handleCancel}
            onFulfill={handleOpenFulfill}
            onMarkUnavailable={handleOpenMarkUnavailable}
            approvingId={requestsMutations.approvingId}
            cancellingId={requestsMutations.cancellingId}
            markingUnavailableId={requestsMutations.markingUnavailableId}
            currentUserId={user?.id}
          />
        </Suspense>
      )}

      {requestsModals.markUnavailableModal !== null && (
        <Suspense fallback={null}>
          <MarkUnavailableModal
            requestId={requestsModals.markUnavailableModal}
            onClose={() => requestsModals.setMarkUnavailableModal(null)}
            onConfirm={handleMarkUnavailableConfirm}
            isLoading={requestsMutations.markingUnavailableId !== null}
          />
        </Suspense>
      )}

      {requestsModals.fulfillModal !== null && (
        <Suspense fallback={null}>
          <FulfillRequestModal
            requestId={requestsModals.fulfillModal}
            onClose={() => requestsModals.setFulfillModal(null)}
            onConfirm={handleFulfillConfirm}
            isLoading={requestsMutations.fulfillingId !== null}
          />
        </Suspense>
      )}
    </>
  );
}
